import { useState } from 'react';
import { RideFormData, DriverInfo } from '@/types/ride';
import { useToast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import { geocode, getRoute } from '@parleyride/hooks';
import { getErrorMessage } from '@/utils/errors';

interface CreateResult {
  success: boolean;
  rideId?: string;
}

export const useRideCreation = () => {
  const [rideId, setRideId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  // Second argument is kept for backwards compatibility with call sites that
  // pass a mock `driverInfo` — it's ignored now that rides are created with
  // status='requested' before any driver exists. The pre-seeded negotiation /
  // price_offers / messages rows are also gone; drivers trigger those when
  // they pick up a ride.
  const createRideRecord = async (
    _driverInfo: DriverInfo | null,
    rideDetails: RideFormData,
  ): Promise<CreateResult> => {
    try {
      setIsLoading(true);

      const pickupFeatures = await geocode(supabase, rideDetails.pickupLocation);
      const destinationFeatures = await geocode(supabase, rideDetails.destination ?? rideDetails.dropoffLocation);
      const pickupCoords = pickupFeatures[0]?.center;
      const destinationCoords = destinationFeatures[0]?.center;
      if (!pickupCoords || !destinationCoords) {
        throw new Error('Could not geocode pickup or destination');
      }

      const route = await getRoute(supabase, pickupCoords, destinationCoords, true).catch(() => null);

      const { data, error } = await supabase.rpc('create_ride', {
        p_pickup_location: rideDetails.pickupLocation,
        p_pickup_lat: pickupCoords[1],
        p_pickup_lng: pickupCoords[0],
        p_destination: rideDetails.destination ?? rideDetails.dropoffLocation,
        p_destination_lat: destinationCoords[1],
        p_destination_lng: destinationCoords[0],
        p_route_distance_meters: route ? Math.round(route.distance) : undefined,
        p_route_duration_seconds: route ? Math.round(route.duration) : undefined,
        p_route_polyline: route?.polyline ?? undefined,
        p_estimated_market_rate:
          typeof rideDetails.estimatedPrice === 'number' ? rideDetails.estimatedPrice : undefined,
        p_vehicle_type_preference: rideDetails.rideType ?? undefined,
        p_scheduled_time: rideDetails.scheduledTime?.toISOString() ?? undefined,
      });
      if (error) throw error;

      const newId = (data as { id: string } | null)?.id;
      if (!newId) throw new Error('create_ride RPC returned no id');

      setRideId(newId);
      return { success: true, rideId: newId };
    } catch (error) {
      console.error('Error creating ride:', error);
      toast({
        title: 'Error creating ride',
        // `create_ride` is a PostgREST RPC, so a failure is thrown as the
        // plain `{ message, details, hint, code }` body — not an `Error`. An
        // `instanceof Error` gate here threw the reason away and showed the
        // generic line instead.
        description: getErrorMessage(error) || 'Could not create ride',
        variant: 'destructive',
      });
      return { success: false };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    rideId,
    setRideId,
    isLoading,
    createRideRecord,
  };
};
